// src/components/CourseManagement.js — calm content-editor index
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
    Edit3, FileText, Settings, ChevronRight, LayoutGrid, List,
} from 'lucide-react';
import { apiService, handleApiError } from '../services/api';
import Loading from './common/Loading';
import { Button, Card, Breadcrumbs, LevelDot } from './ui';

// Map content status → token. "Status cues via small dots" per design system.
const STATUS = {
    complete: { dot: 'good',    label: 'Complete' },
    draft:    { dot: 'warn',    label: 'Draft' },
    empty:    { dot: 'faint',   label: 'Empty' },
};

export default function CourseManagement() {
    const { courseId } = useParams();
    const [course, setCourse] = useState(null);
    const [contentStatus, setContentStatus] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [viewMode, setViewMode] = useState('grid');

    const loadCourse = useCallback(async () => {
        try {
            const response = await apiService.getCourse(courseId);
            setCourse(response.data);
        } catch (err) {
            setError(handleApiError(err));
        }
    }, [courseId]);

    const loadContentStatus = useCallback(async () => {
        try {
            const response = await apiService.getContentStatus(courseId);
            // The API returns ContentStatusResponse:
            //   { course_id, content_status: { "domain_concept": "complete", ... },
            //     total_concepts, completed_concepts, ... }
            // The actual map is at .content_status — without this destructuring
            // every lookup falls through to 'empty' and the page reports 0%
            // even when content exists.
            setContentStatus(response.data?.content_status || {});
        } catch {
            // Non-fatal — concepts default to 'empty' if status fetch fails.
        } finally {
            setLoading(false);
        }
    }, [courseId]);

    useEffect(() => {
        loadCourse();
        loadContentStatus();
    }, [loadCourse, loadContentStatus]);

    // Refetch content status when the user returns to this page from the
    // editor — covers bfcache restores, tab refocus, and the "I just saved
    // a concept and came back, why does it still say empty" case.
    useEffect(() => {
        const refresh = () => {
            if (document.visibilityState === 'visible') loadContentStatus();
        };
        window.addEventListener('focus', refresh);
        document.addEventListener('visibilitychange', refresh);
        return () => {
            window.removeEventListener('focus', refresh);
            document.removeEventListener('visibilitychange', refresh);
        };
    }, [loadContentStatus]);

    const getConceptStatus = (domainKey, concept) =>
        contentStatus[`${domainKey}_${concept}`] || 'empty';

    const calculateDomainProgress = (domain) => {
        if (!domain.concepts.length) return 0;
        const completed = domain.concepts.filter(
            (c) => getConceptStatus(domain.key, c) === 'complete'
        ).length;
        return Math.round((completed / domain.concepts.length) * 100);
    };

    const calculateOverallProgress = () => {
        if (!course?.domains) return 0;
        const total = course.domains.reduce((s, d) => s + d.concepts.length, 0);
        if (!total) return 0;
        const done = course.domains.reduce(
            (s, d) =>
                s + d.concepts.filter((c) => getConceptStatus(d.key, c) === 'complete').length,
            0
        );
        return Math.round((done / total) * 100);
    };

    if (loading) return <Loading message="Loading course content..." />;

    if (error) {
        return (
            <div className="min-h-screen bg-surface-muted flex items-center justify-center px-6">
                <div className="text-center space-y-4 max-w-md">
                    <p className="text-read">{error}</p>
                    <Button variant="primary" onClick={loadCourse}>Try again</Button>
                </div>
            </div>
        );
    }

    const overallProgress = calculateOverallProgress();
    const totalConcepts = course?.domains?.reduce((s, d) => s + d.concepts.length, 0) || 0;
    const completeCount = course?.domains?.reduce(
        (s, d) => s + d.concepts.filter((c) => getConceptStatus(d.key, c) === 'complete').length,
        0
    ) || 0;

    return (
        <div className="min-h-screen bg-surface-muted">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 space-y-8">

                <Breadcrumbs items={[
                    { label: 'My Courses', to: '/my-courses' },
                    { label: course?.name, to: `/course/${courseId}` },
                    { label: 'Manage' },
                ]} />

                {/* Pass 1 — overview */}
                <div className="space-y-2">
                    <div className="flex items-baseline gap-3">
                        <span className="text-5xl font-bold text-ink tabular-nums">
                            {overallProgress}<span className="text-4xl">%</span>
                        </span>
                        <span className="text-mutedink">
                            of {totalConcepts} concepts have content
                            {completeCount !== overallProgress * totalConcepts / 100 && (
                                <span className="text-faint"> · {completeCount} marked complete</span>
                            )}
                        </span>
                    </div>
                </div>

                {/* Pass 2 — primary action: bulk generation */}
                <Link
                    to="#"
                    onClick={(e) => e.preventDefault()}
                    className="group flex items-center justify-between bg-accent-soft hover:bg-accent-subtle border border-accent-subtle rounded-xl px-6 py-5 transition-colors cursor-pointer"
                >
                    <div>
                        <div className="text-base font-semibold text-ink">
                            Generate content for all empty concepts
                        </div>
                        <div className="text-sm text-mutedink mt-0.5">
                            One pass: drafts learning material for every concept that has no content yet.
                        </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-accent group-hover:translate-x-0.5 transition-transform" />
                </Link>

                {/* View mode toggle — quiet, near the section */}
                <div className="flex items-center justify-between">
                    <h2 className="text-xs uppercase tracking-wide text-faint font-normal">
                        Domains ({course?.domains?.length || 0})
                    </h2>
                    <div className="flex items-center gap-1 bg-surface-subtle p-0.5 rounded-md">
                        <button
                            onClick={() => setViewMode('grid')}
                            className={`p-1.5 rounded transition-colors ${
                                viewMode === 'grid'
                                    ? 'bg-surface text-ink shadow-sm'
                                    : 'text-mutedink hover:text-ink'
                            }`}
                            aria-label="Grid view"
                        >
                            <LayoutGrid size={14} />
                        </button>
                        <button
                            onClick={() => setViewMode('list')}
                            className={`p-1.5 rounded transition-colors ${
                                viewMode === 'list'
                                    ? 'bg-surface text-ink shadow-sm'
                                    : 'text-mutedink hover:text-ink'
                            }`}
                            aria-label="List view"
                        >
                            <List size={14} />
                        </button>
                    </div>
                </div>

                {/* Pass 3 — domain sections */}
                <div className="space-y-8">
                    {course?.domains?.map((domain) => {
                        const progress = calculateDomainProgress(domain);
                        const completeInDomain = domain.concepts.filter(
                            (c) => getConceptStatus(domain.key, c) === 'complete'
                        ).length;
                        return (
                            <div key={domain.key} className="space-y-4">

                                {/* Domain header — calm row, no big icon block */}
                                <div className="flex items-baseline justify-between gap-4 flex-wrap">
                                    <div className="flex items-baseline gap-3 min-w-0">
                                        <h3 className="text-lg font-semibold text-ink truncate">
                                            {domain.name}
                                        </h3>
                                        <span className="text-sm text-faint">
                                            {completeInDomain}/{domain.concepts.length} · {domain.weight_percentage || Math.round(domain.weight * 100)}%
                                        </span>
                                    </div>

                                    <div className="flex items-center gap-4 flex-shrink-0">
                                        <div className="flex items-center gap-2">
                                            <div className="w-24 bg-line-subtle rounded-full h-1 overflow-hidden">
                                                <div
                                                    className="bg-accent h-1 transition-all duration-500"
                                                    style={{ width: `${progress}%` }}
                                                />
                                            </div>
                                            <span className="text-sm text-read tabular-nums w-9 text-right">
                                                {progress}%
                                            </span>
                                        </div>
                                        <button className="text-sm text-mutedink hover:text-ink transition-colors">
                                            Generate
                                        </button>
                                    </div>
                                </div>

                                {/* Concepts — grid or list */}
                                {viewMode === 'grid' ? (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                        {domain.concepts.map((concept) => {
                                            const status = getConceptStatus(domain.key, concept);
                                            const cfg = STATUS[status] || STATUS.empty;
                                            return (
                                                <Link
                                                    key={concept}
                                                    to={`/course/${courseId}/manage/${encodeURIComponent(domain.key)}/${encodeURIComponent(concept)}`}
                                                    className="group bg-surface border border-line hover:border-line-strong rounded-lg p-4 transition-colors"
                                                >
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <LevelDot color={cfg.dot} />
                                                        <span className="text-xs text-mutedink">{cfg.label}</span>
                                                    </div>
                                                    <div className="flex items-center justify-between gap-2">
                                                        <span className="text-sm font-medium text-ink truncate">
                                                            {concept.replace(/_/g, ' ')}
                                                        </span>
                                                        <ChevronRight size={14} className="text-faint group-hover:text-mutedink transition-colors flex-shrink-0" />
                                                    </div>
                                                </Link>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <Card>
                                        {domain.concepts.map((concept, i) => {
                                            const status = getConceptStatus(domain.key, concept);
                                            const cfg = STATUS[status] || STATUS.empty;
                                            return (
                                                <Link
                                                    key={concept}
                                                    to={`/course/${courseId}/manage/${encodeURIComponent(domain.key)}/${encodeURIComponent(concept)}`}
                                                    className={`group flex items-center justify-between gap-3 px-4 py-3 hover:bg-surface-muted transition-colors ${
                                                        i === 0 ? 'rounded-t-xl' : ''
                                                    } ${
                                                        i === domain.concepts.length - 1 ? 'rounded-b-xl' : 'border-b border-line-subtle'
                                                    }`}
                                                >
                                                    <div className="flex items-center gap-3 min-w-0">
                                                        <LevelDot color={cfg.dot} />
                                                        <span className="text-sm text-ink truncate">
                                                            {concept.replace(/_/g, ' ')}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-3 text-xs text-mutedink flex-shrink-0">
                                                        <span>{cfg.label}</span>
                                                        <span className="inline-flex items-center gap-1 group-hover:text-ink transition-colors">
                                                            <Edit3 size={12} /> {status === 'empty' ? 'Create' : 'Edit'}
                                                        </span>
                                                        <ChevronRight size={14} className="text-faint group-hover:text-mutedink transition-colors" />
                                                    </div>
                                                </Link>
                                            );
                                        })}
                                    </Card>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Bottom actions — secondary buttons */}
                <div className="pt-6 border-t border-line flex flex-wrap gap-3">
                    <Button to={`/course/${courseId}`} variant="secondary">
                        Course dashboard
                    </Button>
                    <Button variant="secondary" onClick={() => {}}>
                        <FileText className="w-4 h-4" /> Export
                    </Button>
                    <Button variant="ghost" onClick={() => {}}>
                        <Settings className="w-4 h-4" /> Settings
                    </Button>
                </div>
            </div>
        </div>
    );
}
